import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Send } from "lucide-react";
import { trackQrScan } from "@/lib/analytics";

const API = `${window.location.origin}/api`;

const GOLD = "#C4A671";
const CREAM = "#F2EFE8";

const WINES = {
  scilla: {
    name: "Scilla",
    denom: "Inzolia · Terre Siciliane I.G.T.",
    tagline: "Il mito che incontra il mare.",
  },
  "terra-arsa": {
    name: "Terra Arsa",
    denom: "Nero d'Avola · Sicilia D.O.C.",
    tagline: "La siccità trasformata in eleganza.",
  },
  blanche: {
    name: "Blanche",
    denom: "Blanc de Noirs & Blend d'Autore · Limited Edition",
    tagline: "Una di sole 1.333 bottiglie al mondo.",
    gold: true,
  },
  claire: {
    name: "Claire",
    denom: "Rosato di Frappato & Nero d'Avola · Micro-lotto",
    tagline: "L'altra faccia della luce.",
    gold: true,
  },
};

export default function Concierge() {
  const { wineId } = useParams();
  const wine = WINES[wineId];
  const [role, setRole] = useState(null); // "sommelier" | "guest"

  useEffect(() => {
    if (wine) trackQrScan(wineId);
  }, [wineId, wine]);

  if (!wine) {
    return (
      <main className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center px-6 text-center" data-testid="concierge-not-found">
        <p className="overline text-[10px]" style={{ color: GOLD }}>La Valle degli Iblei</p>
        <h1 className="font-serif-display text-3xl mt-4" style={{ color: CREAM }}>Referenza non trovata.</h1>
        <Link to="/" className="mt-8 overline text-[10px] underline" style={{ color: GOLD }} data-testid="concierge-back-home">
          Torna alla Maison
        </Link>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#0A0A0A] text-[#F2EFE8] flex flex-col" data-testid={`concierge-page-${wineId}`}>
      <div className="absolute inset-0 grain opacity-25 pointer-events-none" aria-hidden />
      <AnimatePresence mode="wait">
        {!role ? (
          <RoleGate key="gate" wine={wine} onSelect={setRole} />
        ) : (
          <ConciergeChat key="chat" wineId={wineId} wine={wine} role={role} />
        )}
      </AnimatePresence>
    </main>
  );
}

function RoleGate({ wine, onSelect }) {
  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.7 }}
      className="relative flex-1 flex flex-col items-center justify-center px-6 py-16 text-center"
      data-testid="concierge-role-gate"
    >
      <p className="overline text-[9px]" style={{ color: GOLD, letterSpacing: "0.34em" }}>
        La Valle degli Iblei · Concierge Digitale
      </p>

      <div className="mt-10 mb-2 h-px w-10 mx-auto" style={{ background: GOLD }} />

      <h1
        className="font-serif-display leading-[0.95]"
        style={{ fontSize: "clamp(2.6rem, 10vw, 4.6rem)", color: wine.gold ? GOLD : CREAM }}
        data-testid="concierge-wine-name"
      >
        {wine.name}
      </h1>
      <p className="overline text-[9px] mt-4" style={{ color: "#8B8577" }}>
        {wine.denom}
      </p>
      <p className="font-serif-display italic text-base mt-3" style={{ color: "#C9C3B5" }}>
        {wine.tagline}
      </p>

      <h2 className="font-serif-display italic mt-14 text-lg" style={{ color: CREAM }}>
        Chi sta celebrando questo calice?
      </h2>

      <div className="mt-8 flex flex-col gap-4 w-full max-w-xs">
        <button
          onClick={() => onSelect("sommelier")}
          className="px-8 py-4 border transition-colors duration-300 hover:bg-[#C4A671]/10"
          style={{ borderColor: GOLD }}
          data-testid="concierge-role-sommelier"
        >
          <span className="overline text-[10px]" style={{ color: GOLD }}>
            Sono il Sommelier / Staff di Sala
          </span>
        </button>
        <button
          onClick={() => onSelect("guest")}
          className="px-8 py-4 border border-[#2a2a2a] transition-colors duration-300 hover:border-[#C4A671]/60"
          data-testid="concierge-role-guest"
        >
          <span className="overline text-[10px]" style={{ color: CREAM }}>
            Sono un Ospite al Tavolo
          </span>
        </button>
      </div>

      <Link to="/" className="mt-14 overline text-[8.5px] underline" style={{ color: "#6b6558" }} data-testid="concierge-visit-site">
        Scopra la Maison — lavalledegliiblei.it
      </Link>
    </motion.section>
  );
}

function ConciergeChat({ wineId, wine, role }) {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [streamed, setStreamed] = useState("");
  const [showCta, setShowCta] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/concierge/welcome?wine=${wineId}&role=${role}`);
        const data = await r.json();
        setMessages([{ role: "assistant", content: data.message }]);
      } catch {
        setMessages([{ role: "assistant", content: "Benvenuto. Sono il Concierge de La Valle degli Iblei: come posso accompagnare questo calice?" }]);
      }
    })();
  }, [wineId, role]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streamed, showCta]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setDraft("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    setStreamed("");
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          message: text,
          concierge_wine: wineId,
          concierge_role: role,
        }),
      });
      if (!resp.ok || !resp.body) throw new Error("Bad response");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let assembled = "";
      let trigger = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split(/\n\n/);
        buffer = parts.pop() || "";
        for (const raw of parts) {
          let event = "message";
          let dataStr = "";
          for (const line of raw.split("\n")) {
            if (line.startsWith("event:")) event = line.slice(6).trim();
            else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
          }
          if (!dataStr) continue;
          let payload = {};
          try { payload = JSON.parse(dataStr); } catch { continue; }
          if (event === "session" && payload.session_id) setSessionId(payload.session_id);
          else if (event === "done") trigger = !!payload.trigger_lead_form;
          else if (event === "error") throw new Error(payload.error || "Errore");
          else if (payload.delta) { assembled += payload.delta; setStreamed(assembled); }
        }
      }
      setMessages((m) => [...m, { role: "assistant", content: assembled.trim() }]);
      setStreamed("");
      if (trigger) setShowCta(true);
    } catch {
      setStreamed("");
      setMessages((m) => [...m, { role: "assistant", content: "La connessione si è interrotta. La preghiamo di riprovare tra un istante." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="relative flex-1 flex flex-col max-w-2xl w-full mx-auto px-5 pt-8 pb-5"
      data-testid="concierge-chat"
    >
      {/* Header */}
      <header className="text-center pb-5 border-b border-[#1f1f1f]">
        <p className="overline text-[8.5px]" style={{ color: GOLD, letterSpacing: "0.3em" }}>
          Concierge Digitale
        </p>
        <h1 className="font-serif-display text-2xl mt-1" style={{ color: wine.gold ? GOLD : CREAM }}>
          {wine.name}
        </h1>
        <p className="overline text-[8px] mt-1" style={{ color: "#6b6558" }}>
          {role === "sommelier" ? "Servizio di Sala" : "Ospite al Tavolo"}
        </p>
      </header>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto py-6 space-y-5" data-testid="concierge-messages">
        {messages.map((m, i) => (
          <Bubble key={i} role={m.role} content={m.content} />
        ))}
        {streamed && <Bubble role="assistant" content={streamed} />}
        {sending && !streamed && (
          <p className="font-serif-display italic text-sm" style={{ color: "#6b6558" }}>
            Il Concierge sta scrivendo…
          </p>
        )}
        {showCta && (
          <div className="border p-5 text-center" style={{ borderColor: GOLD }} data-testid="concierge-lead-cta">
            <p className="text-[13.5px] font-light" style={{ color: "#C9C3B5" }}>
              Desidera essere ricontattato personalmente da Claudio Pucci?
            </p>
            <Link
              to="/#contatti"
              className="inline-block mt-4 px-7 py-3 overline text-[9.5px]"
              style={{ background: GOLD, color: "#0A0A0A" }}
              data-testid="concierge-lead-cta-link"
            >
              Lasci i suoi contatti →
            </Link>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="flex items-end gap-3 border-t border-[#1f1f1f] pt-4">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={1}
          placeholder="Scriva al Concierge…"
          className="flex-1 bg-transparent resize-none outline-none text-[15px] font-light placeholder:text-[#5a554a]"
          style={{ color: CREAM }}
          data-testid="concierge-input"
        />
        <button
          onClick={send}
          disabled={sending || !draft.trim()}
          className="p-3 border transition-colors duration-300 disabled:opacity-40 hover:bg-[#C4A671]/10"
          style={{ borderColor: GOLD }}
          aria-label="Invia"
          data-testid="concierge-send-button"
        >
          <Send size={15} color={GOLD} />
        </button>
      </div>
    </motion.section>
  );
}

function Bubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] px-4 py-3 text-[14.5px] leading-[1.75] font-light whitespace-pre-wrap ${isUser ? "border border-[#2a2a2a]" : ""}`}
        style={{
          color: isUser ? CREAM : "#C9C3B5",
          background: isUser ? "rgba(255,255,255,0.03)" : "transparent",
        }}
      >
        {content}
      </div>
    </div>
  );
}
